import { useState, useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';

import { PencilIcon, XMarkIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { ReactSVG } from "react-svg";

import { getStateLabel, formatToYYYY_MMM_DD, getTimeZoneLabel, getStatusReasonLabel, formatDateToDD_MMM_YYYY } from "../../../helpers/functions"
import { getPermissionIdForRole } from "../../../helpers/rolePermissionMapping";
import CommonSlider from '../../common/CommonSlider';
import PersonalInfoEdit from './PersonalInfoEdit';
import ContactInfoEdit from './ContactInfoEdit';

const MemberDetails = ({ user }) => {

    const [showEditSliderForPersonalInfo, setShowEditSliderForPersonalInfo] = useState(false);
    const [showEditSliderForContactInfo, setShowEditSliderForContactInfo] = useState(false);
    const [showDriverLicence, setShowDriverLicence] = useState(false);
    const [showSecondaryGovIDNumber, setShowSecondaryGovIDNumber] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [userObj, setUser] = useState(user);

    // Get data from Redux store (loaded once by MyProfile on page load)
    const { roles } = useSelector((state) => state.roles || []);
    const { companyRoles } = useSelector((state) => state.companyRoles || []);
    const { permissions } = useSelector((state) => state.permissions || []);

    // Simple lookup functions
    const getCompanyRoleName = (id) => {
        const role = companyRoles?.find(r => r.id === id);
        return role?.companyRole || 'Not assigned';
    };
    const getRoleName = (id) => {
        const role = roles?.find(r => r.roleId === id);
        return role?.roleName || 'Not assigned';
    };

    const getPermissionName = (id) => {
        const permission = permissions?.find(p => p.id === id);
        return permission?.permission || 'Not assigned';
    };

    // Common handler for editing information
    const handleEditInfo = (e, sliderType) => {
        e.stopPropagation();
        const currentCompanyRole = companyRoles?.find(r => r.id === user.companyRoleId);
        const derivedPermissionId = getPermissionIdForRole(currentCompanyRole?.role?.roleName || "", permissions || []);
        setUser({
            ...user,
            companyRoleId: currentCompanyRole?.companyRole ?? "",
            roleId: currentCompanyRole?.role?.roleName ?? "",
            permissionId: derivedPermissionId ?? "",
        });
        if (sliderType === 'personal') {
            setShowEditSliderForPersonalInfo(true);
        } else if (sliderType === 'contact') {
            setShowEditSliderForContactInfo(true);
        }
    };

    // Handle edit for personal information
    const handleEditPersonalInfo = (e) => handleEditInfo(e, 'personal');

    // Handle edit for contact information
    const handleEditContactInfo = (e) => handleEditInfo(e, 'contact');

    // Common function to handle slider
    const renderEditSlider = (type) => {
        const steps = [
            { id: 1, name: 'Personal Information' },
            { id: 2, name: 'Contact Information' },
        ];

        const getSliderState = () => {
            switch (type) {
                case 'personal':
                    return showEditSliderForPersonalInfo;
                case 'contact':
                    return showEditSliderForContactInfo;
                default:
                    return false;
            }
        };

        const setSliderState = (value) => {
            switch (type) {
                case 'personal':
                    setShowEditSliderForPersonalInfo(value);
                    break;
                case 'contact':
                    setShowEditSliderForContactInfo(value);
                    break;
            }
        };

        const getStepComponent = () => {
            switch (type) {
                case 'personal':
                    return (
                        <PersonalInfoEdit
                            user={userObj}
                            setUser={setUser}
                            currentStep={currentStep}
                            setCurrentStep={setCurrentStep}
                            isEditMode={true}
                            onClose={() => setSliderState(false)}
                        />
                    );
                case 'contact':
                    return (
                        <ContactInfoEdit
                            user={userObj}
                            setUser={setUser}
                            currentStep={currentStep}
                            setCurrentStep={setCurrentStep}
                            onClose={() => setSliderState(false)}
                            isEditMode={true}
                        />
                    );
                default:
                    return null;
            }
        };

        return (
            <CommonSlider open={getSliderState()} onClose={() => setSliderState(false)}>
                <div className="flex flex-col flex-1 h-full overflow-y-auto">
                    <div className="relative flex-1">
                        {/* Header */}
                        <div className="w-full h-[101px] bg-gray-20 flex justify-between items-center px-[30px] py-6">
                            <div className="flex flex-col">
                                <div className="flex pb-2 space-x-1 font-bold">
                                    <p className="text-gray-800 text-display-xs">
                                        Edit
                                    </p>
                                    <p className="flex items-center gap-2 text-gray-800 text-display-xs">
                                        {type == 'personal' ? steps[0].name : steps[1].name}
                                    </p>
                                </div>
                                <div className="flex flex-row items-center">
                                    <p className="text-gray-900 text-sm font-normal">{user?.lastName}, {user?.firstName}</p>
                                </div>
                            </div>
                            <div className="flex items-center h-7">
                                <button
                                    type="button"
                                    className="flex items-center justify-center p-1 text-teal-300 transition-all bg-blue-200 rounded-full cursor-pointer w-8 h-8 hover:bg-REELTeal hover:text-white"
                                    onClick={() => setSliderState(false)}
                                >
                                    <XMarkIcon
                                        className="w-5 h-5 m-auto"
                                        strokeWidth="2"
                                        alt="Close Panel"
                                    />
                                </button>
                            </div>
                        </div>
                        <div className="px-8 pt-4">
                            {getStepComponent()}
                        </div>
                    </div>
                </div>
            </CommonSlider>
        );
    };

    return (
        <div className="flex flex-row w-full h-full gap-2 py-4">
            {/* Personal Information */}
            <div className="mainCard">
                <div className="flex items-center justify-between w-full px-4 py-3 border-b border-gray-100 bg-gray-20">
                    <h4 className="text-lg font-bold text-teal-600">
                        Personal Information
                    </h4>
                    <Menu as="div" className="relative">
                        <Menu.Button onClick={(e) => e.stopPropagation()} className="inline-flex justify-center px-1 py-1 rounded-md hover:bg-gray-50">
                            <ReactSVG src="/icons/triple-dot.svg" className="flex text-black !items-center w-6 scale-x-[1.4] scale-y-[1.4]" />
                        </Menu.Button>
                        <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform translate-y-2 opacity-0 scale-95" enterTo="transform translate-y-0 opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform translate-y-0 opacity-100 scale-100" leaveTo="transform translate-y-2 opacity-0 scale-95">
                            <Menu.Items className="absolute right-0 top-full mt-1 min-w-[80px] w-auto origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-[1000]">
                                <Menu.Item>
                                    <button className="block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-100" onClick={handleEditPersonalInfo}>
                                        Edit
                                    </button>
                                </Menu.Item>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </div>
                {/* Content */}
                <div className="flex-1 px-4 py-3.5 w-full overflow-y-auto pb-20">
                    <div className="grid grid-cols-2">
                        <div className="col-span-full">
                            <p className="mb-1.5 text-sm text-gray-700">Last Name</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.lastName}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">First Name</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.firstName}
                            </p>
                        </div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Middle Name</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.middleName}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Driver Licence</p>
                            <div className="flex items-center gap-2">
                                {user.driverLicence && (
                                    <button type="button" onClick={() => setShowDriverLicence(!showDriverLicence)} className="text-gray-400 hover:text-gray-600 focus:outline-none">
                                        {showDriverLicence ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                                    </button>
                                )}
                                <p className="font-bold text-gray-800 text-body-md">
                                    {showDriverLicence ? (user.driverLicence || 'N/A') : (user.driverLicence ? '••••••••' : 'N/A')}
                                </p>
                            </div>
                        </div>
                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Expiry Date</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.driverLicenceExpiry ? formatDateToDD_MMM_YYYY(user.driverLicenceExpiry) : ''}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Secondary Government ID</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.secondaryGovernmentID || user.SecondaryGovernmentID || ''}
                            </p>
                        </div>
                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Expiry Date</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {(user.secondaryGovernmentIDExpiry || user.SecondaryGovernmentIDExpiry)
                                    ? formatDateToDD_MMM_YYYY(user.secondaryGovernmentIDExpiry || user.SecondaryGovernmentIDExpiry)
                                    : ''}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div className="col-span-full">
                            <p className="mb-1.5 text-sm text-gray-700">Secondary Government ID Number</p>
                            <div className="flex items-center gap-2">
                                {user.secondaryGovernmentIDNumber && (
                                    <button type="button" onClick={() => setShowSecondaryGovIDNumber(!showSecondaryGovIDNumber)} className="text-gray-400 hover:text-gray-600 focus:outline-none">
                                        {showSecondaryGovIDNumber ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                                    </button>
                                )}
                                <p className="font-bold text-gray-800 text-body-md">
                                    {showSecondaryGovIDNumber ? (user.secondaryGovernmentIDNumber || '-') : (user.secondaryGovernmentIDNumber ? '••••••••' : '-')}
                                </p>
                            </div>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div className="col-span-full">
                            <p className="mb-1.5 text-sm text-gray-700">Background Check - Police Clearance</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.backgroundCheckPoliceClearance || '-'}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">HIPPA Compliancy Certification</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.hippaCertification || user.hipaaComplianceCertification || '-'}
                            </p>
                        </div>
                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">HIPPA Compliancy Certification - Expiry Date</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.hippaCertificationExpiryDate ? formatDateToDD_MMM_YYYY(user.hippaCertificationExpiryDate) : '-'}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div className="col-span-full">
                            <p className="mb-1.5 text-sm text-gray-700">Photo</p>
                            {user.photo
                                ? <img src={`data:image/jpeg;base64,${user.photo}`} alt="Profile" className="max-h-24 rounded object-contain border border-gray-200" />
                                : <p className="font-normal text-gray-400 text-body-md">No photo uploaded</p>
                            }
                        </div>
                    </div>
                </div>
                <div className="px-4 py-3 bg-gray-10">
                    <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                    <p className="mb-1.5 text-sm text-gray-700">Comments</p>
                    <p className="font-bold text-gray-800 text-body-md">
                        {user.personalInfoComments || ''}
                    </p>
                </div>
            </div>

            {/* Contact information */}
            <div className="mainCard">
                <div className="flex items-center justify-between w-full px-4 py-3 border-b border-gray-100 bg-gray-20">
                    <h4 className="text-lg font-bold text-teal-600">
                        Contact Information
                    </h4>
                    <Menu as="div" className="relative">
                        <Menu.Button onClick={(e) => e.stopPropagation()} className="inline-flex justify-center px-1 py-1 rounded-md hover:bg-gray-50">
                            <ReactSVG src="/icons/triple-dot.svg" className="flex text-black !items-center w-6 scale-x-[1.4] scale-y-[1.4]" />
                        </Menu.Button>
                        <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform translate-y-2 opacity-0 scale-95" enterTo="transform translate-y-0 opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform translate-y-0 opacity-100 scale-100" leaveTo="transform translate-y-2 opacity-0 scale-95">
                            <Menu.Items className="absolute right-0 top-full mt-1 min-w-[80px] w-auto origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-[1000]">
                                <Menu.Item>
                                    <button className="block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-100" onClick={handleEditContactInfo}>
                                        Edit
                                    </button>
                                </Menu.Item>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </div>
                {/* Content */}
                <div className="flex-1 px-4 py-3.5 w-full overflow-y-auto pb-20">
                    <div className="grid grid-cols-2 ">
                        <div className="col-span-full">
                            <p className="mb-1.5 text-sm text-gray-700">Email</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.primaryEmail}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Primary Phone</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.primaryPhone}
                            </p>
                        </div>
                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Alternate Contact</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.alternatePhone || '-'}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div className="col-span-full">
                            <p className="mb-1.5 text-sm text-gray-700">Address</p>
                            <p className="font-bold text-gray-800 capitalize text-body-md">
                                {[
                                    user.address && user.address,
                                    user.poBox && `PO Box: ${user.poBox}`,
                                    user.unitNumber && `Unit # ${user.unitNumber}`,
                                ]
                                    .filter(Boolean)
                                    .join(', ')}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">City/Town</p>
                            <p className="font-bold text-gray-800 capitalize text-body-md">
                                {user.city}
                            </p>
                        </div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Province</p>
                            <p className="font-bold text-gray-800 capitalize text-body-md">
                                {getStateLabel(user.province)}
                            </p>
                        </div>

                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Postal Code</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.postalCode}
                            </p>
                        </div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Country</p>
                            <p className="font-bold text-gray-800 capitalize text-body-md">
                                {user.country}
                            </p>
                        </div>

                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div className="col-span-full">
                            <p className="mb-1.5 text-sm text-gray-700">Time Zone</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {getTimeZoneLabel(user.timezone)}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="px-4 py-3 bg-gray-10">
                    <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                    <p className="mb-1.5 text-sm text-gray-700">Comments</p>
                    <p className="font-bold text-gray-800 text-body-md">
                        {user.contactInfoComments || ''}
                    </p>
                </div>
            </div>

            {/* Account information */}
            <div className="mainCard">
                <div className="flex items-center justify-between w-full px-4 py-3 border-b border-gray-100 bg-gray-20">
                    <h4 className="text-lg font-bold text-teal-600">
                        Account Information
                    </h4>
                    {/* Should not be editable */}
                    {/* <PencilIcon
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowEditSliderForAccountInfo(true);
                    }}
                    className="w-5 h-5 text-teal-600 cursor-pointer"
                    /> */}
                </div>
                {/* Content */}
                <div className="flex-1 px-4 py-3.5 w-full overflow-y-auto pb-20">
                    <div className="grid grid-cols-2">
                        <div className="col-span-full">
                            <p className="mb-1.5 text-sm text-gray-700">Company Role</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {getCompanyRoleName(user.companyRoleId)}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Role(s)</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {getRoleName(user.roleId)}
                            </p>
                        </div>
                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Permission</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {getPermissionName(user.permissionId)}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div className="col-span-full">
                            <div className="flex justify-between items-start mb-1.5">
                                <p className="text-sm text-gray-700">Access Tools</p>
                                <p className="text-sm text-gray-700">
                                    Date Added: <span className="text-gray-800 text-body-md">{user.accessToolsDateAdded ? formatDateToDD_MMM_YYYY(user.accessToolsDateAdded) : '-'}</span>
                                </p>
                            </div>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.accessTools?.length > 0 ? user.accessTools.join(', ') : '-'}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Professional Type</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.professionalType || '-'}
                            </p>
                        </div>
                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Professional Type Licence Number</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.professionalTypeLicenceNumber || '-'}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Employment Start</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.employmentStartDate == null
                                    ? ""
                                    : formatDateToDD_MMM_YYYY(user.employmentStartDate)}
                            </p>
                        </div>
                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Employment End</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.employmentEndDate == null
                                    ? ""
                                    : formatDateToDD_MMM_YYYY(user.employmentEndDate)}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">NDA Signed</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.ndaSignedDate == null
                                    ? ""
                                    : formatDateToDD_MMM_YYYY(user.ndaSignedDate)}
                            </p>
                        </div>
                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">NDA Expiry Date</p>
                            <p className="font-bold text-gray-800 text-body-md">
                                {user.ndaExpiryDate == null
                                    ? ""
                                    : formatDateToDD_MMM_YYYY(user.ndaExpiryDate)}
                            </p>
                        </div>
                        <div className="col-span-3 border-b border-b-gray-100 mb-3 pt-2.5"></div>

                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Status</p>
                            <p className={`${user.status == "0" ? 'text-green-500' : 'text-black'} text-body-md font-bold`}>
                                {user.status == "0" ? "Active" : "Inactive"}
                            </p>
                        </div>
                        <div>
                            <p className="mb-1.5 text-sm text-gray-700">Status Reason</p>
                            <p className={`${user.status == "0" ? 'text-green-500' : 'text-black'} text-body-md font-bold`}>
                                {user.statusReason == null ? "" : getStatusReasonLabel(user.statusReason)}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="px-4 py-3 bg-gray-10">
                    <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                    <p className="mb-1.5 text-sm text-gray-700">Comments</p>
                    <p className="font-bold text-gray-800 text-body-md">
                        {user.accountInfoComments || ''}
                    </p>
                </div>
            </div>

            {/* Add the sliders at the end of the component */}
            {renderEditSlider('personal')}
            {renderEditSlider('contact')}
        </div>
    )
}
export default MemberDetails;